import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Container } from '@mui/material';
import { Link } from 'react-router-dom';

export default function SignUp() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: false
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = true;
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = true;
    }
    
    if (!formData.email.trim()) {
      newErrors.email = true;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = true;
    }
    
    if (!formData.password.trim()) {
      newErrors.password = true;
    } else if (formData.password.length < 6) {
      newErrors.password = true;
    }
    
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = true;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignUp = () => {
    if (validateForm()) {
      // TODO: Handle sign up logic
      console.log('Sign up with:', formData);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, display: 'flex' }}>
      {/* Left Side - Hidden on mobile */}
      <Box sx={{ 
        flex: 1, 
        pr: 8,
        display: { xs: 'none', md: 'block' }
      }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 'bold', 
          mb: 4, 
          fontSize: { xs: '2rem', md: '2.5rem' },
          color: '#000000'
        }}>
          Buy, Sell Textbooks with Ease—Connecting Students for a More Affordable Education!
        </Typography>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            color: '#6B89FF', 
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(107, 137, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(107, 137, 255, 0.2)'
          }}>✓</Box>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: '500',
            color: '#2c3e50'
          }}>
            10,000+ textbooks available for exchange
          </Typography>
        </Box>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            color: '#6B89FF', 
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(107, 137, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(107, 137, 255, 0.2)'
          }}>✓</Box>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: '500',
            color: '#2c3e50'
          }}>
            Buy and sell textbooks directly with fellow students
          </Typography>
        </Box>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            color: '#6B89FF', 
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(107, 137, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(107, 137, 255, 0.2)'
          }}>✓</Box>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: '500',
            color: '#2c3e50'
          }}>
            Get the books you need for less—no middleman, no hassle
          </Typography>
        </Box>
      </Box>

      {/* Right Side - Full width on mobile */}
      <Box sx={{ 
        flex: 1,
        width: { xs: '100%', md: 'auto' }
      }}>
        <Typography variant="h4" sx={{ 
          mb: 1, 
          fontWeight: 'bold', 
          textAlign: 'center',
          color: '#000000'
        }}>
          Create account
        </Typography>
        <Typography sx={{ 
          mb: 4, 
          textAlign: 'center',
          color: '#666',
          fontSize: '1.1rem'
        }}>
          Join thousands of students saving money
        </Typography>

        <Box component="form" sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: 3,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(107, 137, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(107, 137, 255, 0.1)'
        }}>
          {/* Name Fields */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1, height: '56px' }}>
              <TextField
                fullWidth
                label="First Name"
                variant="outlined"
                placeholder="Enter your first name"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                error={errors.firstName}
                helperText={errors.firstName ? "First name is required" : " "}
                sx={{
                  '& .MuiFormHelperText-root': {
                    position: 'absolute',
                    bottom: '-20px',
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1, height: '56px' }}>
              <TextField
                fullWidth
                label="Last Name"
                variant="outlined"
                placeholder="Enter your last name"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                error={errors.lastName}
                helperText={errors.lastName ? "Last name is required" : " "}
                sx={{
                  '& .MuiFormHelperText-root': {
                    position: 'absolute',
                    bottom: '-20px',
                  }
                }}
              />
            </Box>
          </Box>

          {/* Email Field */}
          <Box sx={{ height: '56px' }}>
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={errors.email}
              helperText={errors.email ? "Valid email is required" : " "}
              sx={{
                '& .MuiFormHelperText-root': {
                  position: 'absolute',
                  bottom: '-20px',
                }
              }}
            />
          </Box>

          {/* Password Fields */}
          <Box sx={{ height: '56px' }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleInputChange('password')}
              error={errors.password}
              helperText={errors.password ? "Password must be at least 6 characters" : " "}
              sx={{
                '& .MuiFormHelperText-root': {
                  position: 'absolute',
                  bottom: '-20px',
                }
              }}
            />
          </Box>

          <Box sx={{ height: '56px' }}>
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              variant="outlined"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              error={errors.confirmPassword}
              helperText={errors.confirmPassword ? "Passwords must match" : " "}
              sx={{
                '& .MuiFormHelperText-root': {
                  position: 'absolute',
                  bottom: '-20px',
                }
              }}
            />
          </Box>

          <Button
            variant="contained"
            sx={{
              mt: 3,
              background: 'linear-gradient(135deg, #6B89FF 0%, #5A75E6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5A75E6 0%, #4A65D6 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(107, 137, 255, 0.3)'
              },
              textTransform: 'none',
              borderRadius: '30px',
              height: '56px',
              fontSize: '18px',
              fontWeight: '600',
              width: '140px',
              display: 'block',
              mx: 'auto',
              boxShadow: '0 4px 15px rgba(107, 137, 255, 0.2)',
              transition: 'all 0.3s ease'
            }}
            onClick={handleSignUp}
          >
            Sign up
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography sx={{ mb: 1 }}>
              Already have an account? <Link to="/login" style={{ color: '#6B89FF', textDecoration: 'none' }}>Sign in</Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}